import React from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Box, Typography } from "@mui/material";
import Button from "@components/Button/Button";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DRYBULK_CARGO_OPS_LANG_BASE, DRYBULK_CARGO_OPS_ROUTE_SEGMENTS } from "../constants";
import { useAddOperationGate } from "../hooks/useAddOperationGate";

/**
 * DBCO-0028 (R-01, AC1) - *Add operation*, enabled only when the gate says so. When it does not, the
 * server's reason is shown under the button (user-flows step 1: "show the reason") and tied to it
 * with aria-describedby: a disabled button alone cannot explain itself. A failed gate call is
 * closed, never open (HLD D-01).
 */
const AddOperationAction: React.FC<{ vesselImo: string; vesselCallId: string }> = ({
  vesselImo,
  vesselCallId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gate = useAddOperationGate(vesselImo, vesselCallId);

  const enabled = gate.data?.enabled === true;
  let reason: string | null | undefined;
  if (gate.isError) {
    reason = t(`${DRYBULK_CARGO_OPS_LANG_BASE}.add.gate-unavailable`);
  } else if (gate.data && !gate.data.enabled) {
    reason = gate.data.reason;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <Button
        variant="contained"
        startIcon={<AddCircleOutlineIcon />}
        disabled={!enabled}
        aria-describedby={reason ? "drybulk-add-operation-reason" : undefined}
        onClick={() => navigate(DRYBULK_CARGO_OPS_ROUTE_SEGMENTS.addOperation)}
        label={
          <span className="normal-case font-medium">
            {t(`${DRYBULK_CARGO_OPS_LANG_BASE}.add-operation`)}
          </span>
        }
      />
      {reason && (
        <Typography
          id="drybulk-add-operation-reason"
          variant="body2"
          color="text.secondary"
          role="status"
        >
          {reason}
        </Typography>
      )}
    </Box>
  );
};

export default AddOperationAction;
